import Vue from "vue";
import * as themeProduct from "@shopify/theme-product";
import * as currency from "@shopify/theme-currency";
import { ProductForm } from "@shopify/theme-product-form";
import { Constants } from "./quickshop-constants";
import { QuickShopMainVueInstance } from "./index";
import {
  MakeGraphQLQuery,
  setGTMquickShopAdd,
} from "../../utils/helpers";
import { updateCartCounter, updateSampleButtonText, getSampleButtonText, shouldDisabledSampleButton } from "../product-samples/product-samples";

/**
 * The Product QuickShop is a Javascript Class that should be
 * Imported into the appropriate JS components
 */
class QuickShopClass {
  constructor() {
    this.container = Constants.els.quickShopContainer;
    this.closeButton = Constants.els.quickShopCloseBtn;
    this.addToCartBtn = Constants.els.quickShopAddToCartBtn;
    this.productFormSelector = Constants.els.quickShopProductForm;
    this.increaseQtyButton = Constants.els.quickShopQuantityIncrease;
    this.decreaseQtyButton = Constants.els.quickShopQuantityDecrease;
    this.quantityInput = Constants.els.quickShopQuantityInput;
    this.productForm = null;
    this.productJSON = {};
    this.quickShopVueComponent = new Vue(QuickShopMainVueInstance);
  }

  initQuickShopProductForm = (productJSON) => {
    this.productJSON = productJSON;
    const self = this;
    const formElement = document.querySelector(this.productFormSelector);
    const increaseButton = document.querySelector(this.increaseQtyButton);
    const decreaseButton = document.querySelector(this.decreaseQtyButton);
    const closeButton = document.querySelector(this.closeButton);
    if (this.productForm === null) {
      this.productForm = new ProductForm(formElement, productJSON, {
        onOptionChange: self.onOptionChange,
        onQuantityChange: self.onQuantityChange,
        onFormSubmit: self.onFormSubmit,
      });

      // Bind Click Event to Quantity Buttons
      self.addEvent(increaseButton, "click", self.handleQuantityInputChange);
      self.addEvent(decreaseButton, "click", self.handleQuantityInputChange);
      self.addEvent(closeButton, "click", self.handleItemRemove);
      return this.productForm;
    } else {
      // Destroy Old Form & Remove Events
      this.productForm.destroy();
      self.removeEvent(increaseButton, "click", self.handleQuantityInputChange);
      self.removeEvent(decreaseButton, "click", self.handleQuantityInputChange);
      self.removeEvent(closeButton, "click", self.handleItemRemove);

      // Init a new productForm()
      this.productForm = new ProductForm(formElement, productJSON, {
        onOptionChange: self.onOptionChange,
        onQuantityChange: self.onQuantityChange,
        onFormSubmit: self.onFormSubmit,
      });

      // Bind Click Events to Quantity Buttons
      self.addEvent(increaseButton, "click", self.handleQuantityInputChange);
      self.addEvent(decreaseButton, "click", self.handleQuantityInputChange);
      self.addEvent(closeButton, "click", self.handleItemRemove);

      // Return ProductForm
      return this.productForm;
    }
  };

  // Add to QuickShop
  addToQuickshop = async (handle, isSample = false, sampleData = null, options = {}) => {
    try {
      let renderJson = null;
      if (isSample) {
        renderJson = await this.getSampleJSON(handle);
        options = {headerText: 'Confirm Your Sample', themeClass: 'quickshop--sample', ...options};
      } else {
        renderJson = await this.getProductJSON(handle);
      }

      if (!renderJson) {
        console.error(`Product sample not ready: ${handle}`);
        return null;
      }

      const productMetafields = await this.getProductColourMetaFields(
        renderJson
      );
      renderJson.colourSwatchMetafields = productMetafields || [];

      // Set the Product Data within the Component
      this.quickShopVueComponent.$data.product = renderJson;
      // Set custom options for xsell components
      this.quickShopVueComponent.$data.xsellOptions = {
        headerText: options?.headerText || 'Quick Shop',
        themeClass: options?.themeClass || '',
        themeColor: options?.themeColor || {
          theme: '',
          foreground: '',
          background: ''
        },
        isXsellTriggered: !!options.headerText || !!options?.themeClass,
        source: options.source || ''
      };
      if (options.themeColor) {
        const quickshopElement = document.querySelector( '.quickshop__container' );
        if (quickshopElement) {
          if (options.themeColor.theme) {
            quickshopElement.style.setProperty( '--dynamic-theme-color', options.themeColor.theme );
          }
          if (options.themeColor.foreground) {
            quickshopElement.style.setProperty( '--dynamic-theme-foreground', options.themeColor.foreground );
          }
          if (options.themeColor.background) {
            quickshopElement.style.setProperty( '--dynamic-theme-background', options.themeColor.background );
          }
        }
      }
      // Render Product Options
      this.renderProductOptions(renderJson, isSample, sampleData);
      // Render Product Discount Message
      this.renderDiscountMessage(renderJson);
      // Initialise A New Product Form
      this.initQuickShopProductForm(renderJson);
      // Push the GA event
      setGTMquickShopAdd(renderJson.title, options.source);
      // Return Product JSON
      return renderJson;
    } catch (error) {
      return console.error("Error", error);
    }
  };

  // Fetch Data
  getProductJSON = async (productHandle) => {
    try {
      const response = await $.getJSON(`/products/${productHandle}.js`);
      return response;
    } catch (error) {
      return console.error("Error:", error);
    }
  };

  getSampleJSON = async (productHandle) => {
    if (!productHandle) {
      return null;
    }
    let response = await $.getJSON(`/products/${productHandle}.js`)
      .done((productJSON) => {
        return productJSON;
      })
      .fail((err) => {
        console.error('Error:', err);
      });

    return response;
  }

  getProductColourMetaFields = async (productJSON) => {
    const productColourOptionObject = productJSON.options.find(
      (option) => option.name === "Colour"
    );

    if (productColourOptionObject === undefined) {
      return [];
    }

    // const colourOptionNames = productColourOptionObject?.values || [];
    const identifiers = [];

    // colourOptionNames.forEach(colourOptionName => {
    //   const colourOptionNameSnakeCase = toSnakeCase(colourOptionName);
    // });
    const colour_code_metafield_key = "swatch_colour";
    const colour_image_metafield_key = "swatch_media";

    identifiers.push({ namespace: "custom", key: colour_code_metafield_key });
    identifiers.push({ namespace: "custom", key: colour_image_metafield_key });

    const getProductColourMetaFieldsGqlQuery = () => {
      const identifiersClean = JSON.stringify(identifiers);
      const identifiersCleanWithoutKeyQuotes = identifiersClean.replace(
        /"([^(")"]+)":/g,
        "$1:"
      );

      return `
        query getProductColourMetaFields {
          product(handle: "${productJSON.handle}"){
            metafields(identifiers: ${identifiersCleanWithoutKeyQuotes}) {
              namespace	
              key
              value
              references(first: 50) {
                edges {
                  node {
                    ... on MediaImage {
                      image {
                        url
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `;
    };

    if (!identifiers.length) {
      return [];
    }

    const productColourMetaFieldsGqlQuery =
      getProductColourMetaFieldsGqlQuery();

    const response = await MakeGraphQLQuery(
      `${window.theme.storeURL}/api/2026-04/graphql.json`,
      productColourMetaFieldsGqlQuery
    );

    if (response.hasOwnProperty("error")) {
      throw new Error("Failed GraphQL Request");
    }

    const removeNullMetafields = (metafield) => metafield;
    const metafields =
      response.data?.product?.metafields.filter(removeNullMetafields) || [];

    return metafields;
  };

  // Render Methods
  renderProductOptions = (data, isSample = false, sampleData = null) => {
    // Now methods are within Child Component
    // We may have to use $refs to use child methods

    this.quickShopVueComponent.$refs.sidebar.toggleQuickShopOpen(isSample, sampleData);
    this.quickShopVueComponent.$refs.sidebar.setFirstAvailableVariant(data);
    this.quickShopVueComponent.$refs.sidebar.setColourOptions(data);
    this.quickShopVueComponent.$refs.sidebar.setSizeLengthOptions(data);
    this.quickShopVueComponent.$refs.sidebar.setQtyOptions(data);
  };
  renderPrice = (value) => {
    return currency.formatMoney(value);
  };

  // Render the Discount Message bar from product metafields
  renderDiscountMessage(data) {
    const { handle } = data;
    this.quickShopVueComponent.$refs.sidebar.setDiscountMessage(handle);
  }

  // Shopify ProductForm() methods
  // Which are passed through as callback Methods
  onOptionChange = (productJSON, selectedOptions) => {
    if (!productJSON && !selectedOptions) {
      return;
    }

    if (productJSON.variants) {
      const foundVariant = themeProduct.getVariantFromOptionArray(
        productJSON,
        selectedOptions
      );

      if (foundVariant) {
        return foundVariant;
      } else {
        const suggestedVariant = this.findSuggestedVariantBySelectedOptions(
          productJSON.variants,
          selectedOptions
        );
        return suggestedVariant;
      }
    }
  };

  onQuantityChange = () => {
    // We can watch for the quantity check if product is still available
  };

  onFormSubmit = () => { };

  // Find Variant IDs to add to Cart
  getFirstAvailableVariant = (product) => {
    if (!product && !product.variants) {
      return;
    }
    return product.variants[0];
  };
  findSuggestedVariantBySelectedOptions = (
    variants,
    functionSelectedOptions
  ) => {
    const foundVariant = variants.find((variant) =>
      functionSelectedOptions.every((option, i) => {
        return option === variant.options[i];
      })
    );
    return foundVariant
      ? foundVariant
      : this.findSuggestedVariantBySelectedOptions(
        variants,
        functionSelectedOptions.slice(0, functionSelectedOptions.length - 1)
      );
  };

  // Option Methods
  getProductFormSelectedOptions = () => {
    return this.productForm.options();
  };

  handleItemRemove = (e) => {
    updateCartCounter();
    // checkSampleThresholdReached();
  };

  // Quantity Methods
  handleQuantityInputChange = () => {
    const input = document.querySelector(this.quantityInput);
    var value = parseInt(input.value, 10);

    if (event.target.id == "quickshop-quantity-increase") {
      value = isNaN(value) ? 1 : value;
      value++;
    } else if (event.target.id == "quickshop-quantity-decrease") {
      value = isNaN(value) ? 1 : value;
      value--;
    }
    input.value = value < 2 ? 1 : value;

    // Trigger Change Event on Input
    const changeEvent = new Event("change");
    input.dispatchEvent(changeEvent);
    updateCartCounter();
    // Update sample button state after quantity change
    const dataset = document.querySelector('[data-product-form]')?.dataset;
    if (dataset && dataset.sampleVariantId) {
      const isKit = ['true', true].includes(dataset.sampleKit);
      const text = getSampleButtonText(dataset.sampleVariantId, isKit, isKit ? dataset.sampleKitWeight : 1);
      updateSampleButtonText(text, shouldDisabledSampleButton(text));
    }
  };
  getProductFormQuantity = () => {
    return this.productForm.quantity();
  };

  toggleQuickShopOpen = () => {
    this.quickShopVueComponent.$refs.sidebar.toggleQuickShopOpen();
  };

  // Add To Cart
  // addToCart = (
  //   id,
  //   handle,
  //   qty,
  //   properties = {},
  //   options = {}
  // ) => {
  //   // CartJS.addItem(id, qty, properties, options);
  //   addToCart(qty, handle, id)
  // };

  // Listeners
  addEvent = (element, type, event) => {
    element.addEventListener(type, event);
  };
  removeEvent = (element, type, event) => {
    element.removeEventListener(type, event);
  };

  // Get the Quickshop Instance
  static getInstance() {
    if (this.instance === undefined) {
      this.instance = new QuickShopClass();
      return this.instance;
    }
    return this.instance;
  }
}

export default QuickShopClass;
